import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import './Input.css';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helpText?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  helpText,
  error,
  leftIcon,
  rightIcon,
  fullWidth = false,
  className = '',
  id,
  ...props
}, ref) => {
  const { t } = useTranslation();
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  const baseClass = 'input';
  
  const wrapperClasses = [
    `${baseClass}-wrapper`,
    fullWidth && `${baseClass}-wrapper--full-width`,
    error && `${baseClass}-wrapper--error`,
    props.disabled && `${baseClass}-wrapper--disabled`,
  ].filter(Boolean).join(' ');

  const inputClasses = [
    baseClass,
    leftIcon && `${baseClass}--with-left-icon`,
    rightIcon && `${baseClass}--with-right-icon`,
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={inputId} className={`${baseClass}-label`}>
          {label}
          {props.required && <span className={`${baseClass}-required`} aria-label={t('common.required')}>*</span>}
        </label>
      )}
      
      <div className={`${baseClass}-container`}>
        {leftIcon && (
          <span className={`${baseClass}-icon ${baseClass}-icon--left`} aria-hidden="true">
            {leftIcon}
          </span>
        )}
        
        <input
          ref={ref}
          id={inputId}
          className={inputClasses}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            [
              helpText && `${inputId}-help`,
              error && `${inputId}-error`
            ].filter(Boolean).join(' ') || undefined
          }
          {...props}
        />
        
        {rightIcon && (
          <span className={`${baseClass}-icon ${baseClass}-icon--right`} aria-hidden="true">
            {rightIcon}
          </span>
        )}
      </div>
      
      {helpText && !error && (
        <p id={`${inputId}-help`} className={`${baseClass}-help`}>
          {helpText}
        </p>
      )}
      
      {error && (
        <p id={`${inputId}-error`} className={`${baseClass}-error`} role="alert">
          {error}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;